import { type CustomTooltipProps } from "ag-grid-react"
import React, { type PropsWithChildren } from "react"
import FlagsTooltip from "./FlagsTooltip"
import RulesTooltip from "./RulesTooltip"
import TagsTooltip from "./TagsTooltip"
import { UniqueListTooltip } from "./UniqueListTooltip"
import SalesAndForecastTooltip from "./SalesAndForecastTooltip"
import StorageLocationTooltip from "./StorageLocationTooltip"
import IncomingStockTooltip from "./IncomingStockTooltip"
import InventoryValueGraphTooltip from "./InventoryValueGraphTooltip"
import InventoryCostTooltip from "./InventoryCostTooltip"
import LastIncomingStockTooltip from "./LastIncomingStockTooltip"
import NextIncomingStockTooltip from "./NextIncomingStockTooltip"
import ReturnsTooltip from "./ReturnsTooltip"
import SellThroughRateTooltip from "./SellThroughRateTooltip"
import SuggestedOrderTooltip from "./SuggestedOrderTooltip"
import ForecastChangeTooltip from "./ForecastChangeTooltip"
import HeaderTooltip from "./HeaderTooltip"
import BarGraphTooltip from "./BarGraphTooltip"
import StockStatusTooltip from "./StockStatusTooltip"
import AdjustmentTooltip from "./AdjustmentTooltip"
import PreordersTooltip from "./PreordersTooltip"
import SalesPriceMatrixTooltip from "./SalesPriceMatrixTooltip"
import ConstraintAvgPriceTooltip from "./ConstraintAvgPriceTooltip"
import IncomingOrdersCollectionTooltip from "./IncomingOrdersCollectionTooltip"
import NoForecastTooltip from "./NoForecastTooltip"
import SizeSplitConfidenceTooltip from "./SizeSplitConfidenceTooltip"
import SafetyStockTooltip from "./SafetyStockTooltip"
import WeeksOnHandTooltip from "./WeeksOnHandTooltip"
import QuantityAlertsTooltip from "./QuantityAlertsTooltip"
import DelayedPoWarningTooltip from "./DelayedPoWarningTooltip"
import VsExpectedTooltip from "./VsExpectedTooltip"
import RunDriftTooltip from "./RunDriftTooltip"
import LineSettingSourceTooltip from "./LineSettingSourceTooltip"
import LineWarningsTooltip from "./LineWarningsTooltip"
import PerformanceStatusTooltip from "./PerformanceStatusTooltip"
import { formatDate, getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { addDays } from "date-fns"
import TooltipMessageCard from "./TooltipMessageCard"
import { INVENTORY_COST_TOOLTIP_TYPE } from "~/utils/inventoryCalendar"

const tooltipMap: Record<string, React.FC<CustomTooltipProps & any>> = {
  Flags: FlagsTooltip,
  Rules: RulesTooltip,
  IncomingStock: IncomingStockTooltip,
  InventoryValueGraph: InventoryValueGraphTooltip,
  [INVENTORY_COST_TOOLTIP_TYPE]: InventoryCostTooltip,
  LastIncomingStock: LastIncomingStockTooltip,
  NextIncomingStock: NextIncomingStockTooltip,
  Returns: ReturnsTooltip,
  SalesAndForecast: SalesAndForecastTooltip,
  SellThroughRate: SellThroughRateTooltip,
  StorageLocation: StorageLocationTooltip,
  SuggestedOrder: SuggestedOrderTooltip,
  Tags: TagsTooltip,
  UniqueList: UniqueListTooltip,
  ForecastChange: ForecastChangeTooltip,
  BarGraph: BarGraphTooltip,
  StockStatus: StockStatusTooltip,
  Adjustment: AdjustmentTooltip,
  PreOrders: PreordersTooltip,
  SalesPriceMatrix: SalesPriceMatrixTooltip,
  ConstraintAvgPrice: ConstraintAvgPriceTooltip,
  PurchaseOrderCollection: IncomingOrdersCollectionTooltip,
  NoForecast: NoForecastTooltip,
  SizeSplitConfidence: SizeSplitConfidenceTooltip,
  SafetyStock: SafetyStockTooltip,
  WeeksOnHand: WeeksOnHandTooltip,
  QuantityAlerts: QuantityAlertsTooltip,
  DelayedPoWarning: DelayedPoWarningTooltip,
  VsExpected: VsExpectedTooltip,
  RunDrift: RunDriftTooltip,
  LineSettingSource: LineSettingSourceTooltip,
  LineWarnings: LineWarningsTooltip,
  PerformanceStatus: PerformanceStatusTooltip,
}

function getDateRangeComponent(dateRangeType: "lastYear" | "nextYear") {
  switch (dateRangeType) {
    case "lastYear":
      return (
        <div className="text-sm mt-1">
          <p>Last 52 weeks:</p>
          <p>
            {formatDate(addDays(getStartOfCurrentWeekUTC(), -(52 * 7)))} -{" "}
            {formatDate(addDays(getStartOfCurrentWeekUTC(), -1))}
          </p>
        </div>
      )
    case "nextYear":
      return (
        <div className="text-sm mt-1">
          <p>Next 52 weeks:</p>
          <p>
            {formatDate(getStartOfCurrentWeekUTC())} -{" "}
            {formatDate(addDays(getStartOfCurrentWeekUTC(), 52 * 7 - 1))}
          </p>
        </div>
      )
    default:
      return undefined
  }
}

export default function CustomAgGridTooltip(
  props: CustomTooltipProps &
    PropsWithChildren & { tooltipType: string; showDateRange?: "lastYear" | "nextYear" },
) {
  const valueTooltipType =
    typeof props.value === "object" && props.value !== null
      ? ((props.value as Record<string, unknown>).tooltipType as string | undefined)
      : undefined
  const tooltipType = props.tooltipType ?? valueTooltipType
  const TooltipComponent = tooltipType ? tooltipMap[tooltipType] : undefined

  const handleMouseLeave = () => {
    if (tooltipType !== "SalesAndForecast" && tooltipType !== "InventoryValueGraph") {
      props.hideTooltipCallback?.()
    }
  }

  if (props.value?.unavailableMessage) {
    return (
      <TooltipMessageCard badge={props.value.unavailableBadge}>
        {props.value.unavailableMessage}
      </TooltipMessageCard>
    )
  }

  if (!TooltipComponent) {
    if (props.location === "header") {
      return (
        <HeaderTooltip {...props}>
          {props.showDateRange && getDateRangeComponent(props.showDateRange)}
        </HeaderTooltip>
      )
    }

    if (props.location === "cell" && props.value && typeof props.value !== "object") {
      return <TooltipMessageCard>{props.value}</TooltipMessageCard>
    }
    return null
  }

  return (
    <div onMouseLeave={handleMouseLeave}>
      <TooltipComponent {...props}>
        {props.showDateRange && getDateRangeComponent(props.showDateRange)}
      </TooltipComponent>
    </div>
  )
}
